import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector, useDispatch } from 'react-redux';
import Button from '@material-ui/core/Button';
import qs from 'query-string';
import clsx from 'clsx';

import { converRGBAalpha } from 'common/utils/colorConvert';
import { fieldWrapperStyle, isEditModeStyle } from 'common/style/fields.style';
import getWrapperWidth from 'editor/components/FormContent/Field/helper';
import selectAll from 'editor/utils/selectAll';
import useSelectedPageInfo from 'common/hooks/useSelectedPageInfo';
import { selectPage } from 'redux/modules/flag';
import { updateFormSetting } from 'redux/modules/settings';
import { setChangedIDs } from 'redux/modules/changedFields';
import { stringBytes } from 'common/utils/caculateStrBtyes';
import useClientTranslation from 'common/hooks/useClientTranslation';

function FormButtons({
    show = true,
    width = 1,
    isSelected,
    disabled,
    disabledChangePage,
    isPageCanChange
}) {
    const t = useClientTranslation();
    const dispatch = useDispatch();
    const isEditMode = !disabled && isSelected;
    const { templateType } = useSelector(state => state.form);
    const { btn_type } = qs.parse(window.location.search);
    let {
        form: { formBtnTextColor, formBtnBgColor, formBtnSubmitText }
    } = useSelector(state => state.settings) || {};
    formBtnTextColor = formBtnTextColor || '#fff';
    formBtnBgColor = formBtnBgColor || '#333';
    const classes = useStyles({
        btnTextColor: formBtnTextColor,
        btnBgColor: formBtnBgColor,
        btnHoverColor: converRGBAalpha(formBtnBgColor, 0.8),
        isSelected: isEditMode
    });
    const { isFirstPage, isLastPage, isOnePage, nextPageId, prevPageId } = useSelectedPageInfo();
    const updateDisplayName = ({ target: { value } }) => {
        if (stringBytes(value) <= 40) {
            dispatch(updateFormSetting({ formBtnSubmitText: value }));
            dispatch(setChangedIDs({ settingsForm: true }));
        }
    };
    const handleSelectPage = pageId => {
        if (!disabledChangePage) {
            dispatch(selectPage(pageId));
        }
    };
    const handlePageChange = async () => {
        if (disabledChangePage || !(await isPageCanChange())) {
            return;
        }
        handleSelectPage(nextPageId);
    };
    if (!show) {
        return false;
    }
    return (
        <div css={css(fieldWrapperStyle)}>
            <div css={css(getWrapperWidth(width), buttonsWrapperStyle)}>
                {templateType === 3 && Boolean(btn_type) && (
                    <Button
                        onClick={() => window.parent.postMessage({ submitted: false }, '*')}
                        className={clsx(classes.button, classes.secondaryButton)}
                    >
                        {t(`button.label.${btn_type}`)}
                    </Button>
                )}
                {!isOnePage && !isFirstPage && (
                    <Button
                        onClick={() => handleSelectPage(prevPageId)}
                        className={clsx(classes.button, classes.secondaryButton)}
                    >
                        {t('button.label.return')}
                    </Button>
                )}
                {!isOnePage && !isLastPage && (
                    <Button
                        onClick={handlePageChange}
                        className={clsx(classes.button, classes.primaryButton)}
                    >
                        {t('button.label.continue')}
                    </Button>
                )}
                {isLastPage && (
                    <Button
                        type="submit"
                        className={clsx(
                            classes.button,
                            classes.primaryButton,
                            classes.editingButton
                        )}
                    >
                        {isEditMode ? (
                            <textarea
                                css={css(isEditModeStyle, textareaStyle)}
                                value={formBtnSubmitText}
                                rows="1"
                                onChange={updateDisplayName}
                                onFocus={selectAll}
                            />
                        ) : (
                            formBtnSubmitText
                        )}
                    </Button>
                )}
            </div>
        </div>
    );
}

FormButtons.propTypes = {
    width: PropTypes.number,
    isSelected: PropTypes.bool,
    disabledChangePage: PropTypes.bool,
    show: PropTypes.bool,
    isPageCanChange: PropTypes.func,
    disabled: PropTypes.bool
};

export default React.memo(FormButtons);

const useStyles = makeStyles({
    button: {
        flexShrink: 0,
        padding: '5px 28px',
        textTransform: 'initial'
    },
    secondaryButton: {
        color: '#333',
        marginRight: '10px',
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.8)'
        }
    },
    primaryButton: props => ({
        color: props.btnTextColor,
        backgroundColor: props.btnBgColor,
        '&:hover': {
            backgroundColor: props.btnHoverColor
        }
    }),
    editingButton: props => ({
        flexGrow: props.isSelected ? 1 : 0,
        '&:hover': {
            backgroundColor: props.btnBgColor
        }
    })
});

const textareaStyle = `
    line-height: 1rem;
    padding: 4px;
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    height: 26px;
    color: inherit;
`;

const buttonsWrapperStyle = `
    display: flex;
`;
